/**
 * Employee data transformation mappers.
 * Provides bidirectional mapping between GraphQL DTOs, form data, and table data shapes.
 * Handles employee CRUD operations, crew roles, and ban entry transformations.
 */
import {
  Dtos_V1_CrewRoleDto,
  Dtos_V1_EmployeeDto,
  EmploymentType,
  Input_V1_Mutations_Employee_CreateEmployeeMutationInput,
  Input_V1_Mutations_Employee_UpdateEmployeeMutationInput,
  Maybe
} from '@/common/infrastructure/gql/generated/schema';
import { EmployeeDetails, EmployeeFormData, EmployeeTableData, JobRoleData, BanEntryApiResponse } from '../types/EmployeeTypes';
import { formatDate } from '@/common/application/utils/timeUtils';

export const mapEmployeeToFormData = (employee?: EmployeeDetails | null): EmployeeFormData => {
  if (!employee)
    return {
      id: undefined,
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      driversLicenseNumber: '',
      driversLicenseState: '',
      jobRolesIds: [],
      employmentType: undefined,
      employmentStatusId: undefined,
      dispatchLocationId: undefined,
      workLocationId: undefined,
      startDate: undefined,
      endDate: undefined,
      notes: '',
      banEntries: []
    };

  return {
    id: employee.id,
    firstName: employee.firstName ?? '',
    lastName: employee.lastName ?? '',
    phoneNumber: employee.phoneNumber?.contact ?? '',
    phoneNumberId: employee.phoneNumber?.id ?? undefined,
    email: employee.email?.contact ?? '',
    emailId: employee.email?.id ?? undefined,
    driversLicenseNumber: employee.driversLicense?.number ?? '',
    driversLicenseState: employee.driversLicense?.state ?? '',
    jobRolesIds: employee.jobRoles?.map((role: Dtos_V1_CrewRoleDto) => role.id) ?? [],
    employmentType: employee.employmentType?.toString(),
    employmentStatusId: employee.employmentStatus?.id ?? undefined,
    dispatchLocationId: employee.dispatchLocation?.id ?? undefined,
    workLocationId: employee.workLocation?.id ?? undefined,
    startDate: employee.startDate ? new Date(employee.startDate) : undefined,
    endDate: employee.endDate ? new Date(employee.endDate) : undefined,
    notes: employee.notes ?? '',
    banEntries:
      (employee.banEntries as BanEntryApiResponse[])?.map((ban: BanEntryApiResponse) => ({
        id: ban.id,
        banType: ban.banType ?? '',
        bannedFrom: {
          id: ban.bannedFrom?.id ?? 0,
          name: ban.bannedFrom?.name ?? ''
        },
        createdBy: {
          id: ban.createdBy?.id ?? 0,
          name: ban.createdBy?.name ?? ''
        },
        createdOn: formatDate(ban.createdOn, 'MM/dd/yyyy') ?? '',
        updatedBy: {
          id: ban.updatedBy?.id ?? 0,
          name: ban.updatedBy?.name ?? ''
        },
        updatedOn: formatDate(ban.updatedOn, 'MM/dd/yyyy') ?? ''
      })) ?? []
  };
};

const hasRole = (jobRoles: { name?: string | null }[] | null | undefined, roleName: string): boolean =>
  (jobRoles ?? []).some((r) => r.name?.toLowerCase().trim() === roleName.toLowerCase());

export const mapEmployeeToTableData = (employee: Dtos_V1_EmployeeDto): EmployeeTableData => {
  const jobRoles = employee.jobRoles ?? [];
  const startDate = employee.startDate;
  const endDate = employee.endDate;
  const updatedOn = employee.updatedOn;

  // Type assertion for extended fields that come from GraphQL query but aren't in base type
  const emp = employee as Dtos_V1_EmployeeDto & {
    eid?: string | null;
    fullName?: string | null;
    usable?: boolean | null;
    inactive?: boolean | null;
    dotHours8Day?: number | null;
    dotHours7Day?: number | null;
    dotHours6Day?: number | null;
    dotHours5Day?: number | null;
    clockStatus?: string | null;
    clockTime?: string | null;
    assignedEquipment?: string | null;
    daysOut?: number | null;
    updatedBy?: { name?: string | null } | null;
  };

  const displayName = employee.name ?? emp.fullName ?? 'N/A';
  return {
    id: employee.id,
    empId: emp.eid ?? String(employee.id),
    name: displayName,
    companyName: displayName,
    jobRoles: mapJobRoles(employee.jobRoles ?? undefined),
    availableJobRoles: mapJobRoles(employee.availableJobRoles ?? undefined),
    employmentStatus: employee.employmentStatus?.name ?? 'N/A',
    phoneNumber: employee.phoneNumber?.contact ?? 'N/A',
    email: employee.email?.contact ?? 'N/A',
    hiredOn: startDate ? (formatDate(startDate as string, 'MM/dd/yyyy') ?? '') : '',
    stationedAt: employee.workLocation?.name ?? '',
    dispatchLocation: employee.dispatchLocation?.name ?? '',
    releasedOn: endDate ? (formatDate(endDate as string, 'MM/dd/yyyy') ?? '') : '',
    seniority: employee.seniorityCode ?? '',
    updatedOn: updatedOn ? (formatDate(updatedOn as string, 'MM/dd/yyyy') ?? '') : '',
    deletable: employee.deletable ?? false,
    usable: emp.usable ?? false,
    inactive: emp.inactive ?? false,
    eightDayDriving: emp.dotHours8Day != null ? String(emp.dotHours8Day) : '',
    sevenDayDriving: emp.dotHours7Day != null ? String(emp.dotHours7Day) : '',
    sixDayDriving: emp.dotHours6Day != null ? String(emp.dotHours6Day) : '',
    fiveDayDriving: emp.dotHours5Day != null ? String(emp.dotHours5Day) : '',
    clockStatus: emp.clockStatus ?? '',
    clockTime: emp.clockTime ? (formatDate(emp.clockTime as string, 'MM/dd/yyyy HH:mm') ?? '') : '',
    assignedEquipment: emp.assignedEquipment ?? '',
    daysOut: emp.daysOut != null ? String(emp.daysOut) : '',
    updatedBy: emp.updatedBy?.name ?? '',
    operator: hasRole(jobRoles, 'operator'),
    laborer: hasRole(jobRoles, 'laborer'),
    swamp: hasRole(jobRoles, 'swamp'),
    salesman1: hasRole(jobRoles, 'salesman1'),
    dispatcher: hasRole(jobRoles, 'dispatcher'),
    mechanic: hasRole(jobRoles, 'mechanic')
  };
};

export const mapFormDataToCreateInput = (formData: EmployeeFormData): Input_V1_Mutations_Employee_CreateEmployeeMutationInput => {
  return {
    ...(formData.firstName && { firstName: formData.firstName }),
    ...(formData.lastName && { lastName: formData.lastName }),
    ...(formData.phoneNumber && { phoneNumber: { contact: formData.phoneNumber } }),
    ...(formData.email && { email: { contact: formData.email } }),
    ...((formData.driversLicenseNumber || formData.driversLicenseState) && {
      driversLicense: {
        number: formData.driversLicenseNumber,
        state: formData.driversLicenseState
      }
    }),
    ...(formData.jobRolesIds?.length > 0 && { jobRolesToAdd: formData.jobRolesIds.map((id) => ({ id })) }),
    ...(formData.employmentType && { employmentType: formData.employmentType as EmploymentType }),
    ...(formData.employmentStatusId && { employmentStatus: { id: formData.employmentStatusId } }),
    ...(formData.dispatchLocationId && { dispatchLocation: { id: formData.dispatchLocationId, default: false } }),
    ...(formData.workLocationId && { workLocation: { id: formData.workLocationId, default: false } }),
    ...(formData.startDate && { startDate: formatDate(formData.startDate, 'yyyy-MM-dd HH:mm:ss') ?? undefined }),
    ...(formData.endDate && { endDate: formatDate(formData.endDate, 'yyyy-MM-dd HH:mm:ss') ?? undefined }),
    ...(formData.notes && { notes: formData.notes })
  };
};

export const mapFormDataToUpdateInput = (formData: EmployeeFormData): Input_V1_Mutations_Employee_UpdateEmployeeMutationInput => {
  if (!formData.id) {
    throw new Error('Employee ID is required for update');
  }
  const jobRolesToDelete = formData?.initialJobRolesIds?.filter((id) => !formData.jobRolesIds.includes(id)) ?? [];

  // Helper function to handle clearable string fields
  const mapClearableStringField = (value: string | null | undefined): string | null => {
    if (value === null || value === undefined || value.trim() === '') {
      return null;
    }
    return value.trim();
  };

  // Helper function to handle clearable numeric/ID fields
  const mapClearableIdField = (value: number | null | undefined): number | null => {
    if (value === null || value === undefined || value === 0) {
      return null;
    }
    return value;
  };

  return {
    id: formData.id,
    // Handle clearable string fields - send null when cleared
    ...(formData.firstName !== undefined && { firstName: mapClearableStringField(formData.firstName) }),
    ...(formData.lastName !== undefined && { lastName: mapClearableStringField(formData.lastName) }),
    // Handle phone number - send null when cleared
    ...(formData.phoneNumber !== undefined && {
      phoneNumber:
        formData.phoneNumber && formData.phoneNumber.trim() ? { id: formData.phoneNumberId, contact: formData.phoneNumber.trim() } : null
    }),
    // Handle email - send null when cleared
    ...(formData.email !== undefined && {
      email: formData.email && formData.email.trim() ? { id: formData.emailId, contact: formData.email.trim() } : null
    }),
    // Handle drivers license - send null when both fields are cleared
    ...(formData.driversLicenseNumber !== undefined || formData.driversLicenseState !== undefined
      ? {
          driversLicense:
            (formData.driversLicenseNumber && formData.driversLicenseNumber.trim()) ||
            (formData.driversLicenseState && formData.driversLicenseState.trim())
              ? {
                  number: formData.driversLicenseNumber?.trim() || null,
                  state: formData.driversLicenseState?.trim() || null
                }
              : null
        }
      : {}),
    ...(formData.jobRolesIds?.length > 0 && { jobRolesToAdd: formData.jobRolesIds.map((id) => ({ id })) }),
    ...(jobRolesToDelete.length > 0 && { jobRolesToDelete }),
    // Handle clearable enum field - send null when cleared
    ...(formData.employmentType !== undefined && {
      employmentType: formData.employmentType ? (formData.employmentType as EmploymentType) : null
    }),
    // Handle clearable ID fields - send null when cleared
    ...(formData.employmentStatusId !== undefined && {
      employmentStatus: (() => {
        const id = mapClearableIdField(formData.employmentStatusId);
        return id !== null ? { id } : null;
      })()
    }),
    ...(formData.dispatchLocationId !== undefined && {
      dispatchLocation: (() => {
        const id = mapClearableIdField(formData.dispatchLocationId);
        return id !== null ? { id, default: false } : null;
      })()
    }),
    ...(formData.workLocationId !== undefined && {
      workLocation: (() => {
        const id = mapClearableIdField(formData.workLocationId);
        return id !== null ? { id, default: false } : null;
      })()
    }),
    // Handle date fields - send null when cleared
    ...(formData.startDate !== undefined && {
      startDate: formData.startDate ? (formatDate(formData.startDate, 'yyyy-MM-dd HH:mm:ss') ?? null) : null
    }),
    ...(formData.endDate !== undefined && {
      endDate: formData.endDate ? (formatDate(formData.endDate, 'yyyy-MM-dd HH:mm:ss') ?? null) : null
    }),
    // Handle clearable string field - send null when cleared
    ...(formData.notes !== undefined && { notes: mapClearableStringField(formData.notes) }),
    ...(formData.outOfOfficesToCreate?.length && {
      outOfOfficesToCreate: formData.outOfOfficesToCreate.map((ooo) => ({
        employee: { id: ooo.employee!.id },
        startDate: ooo.start,
        endDate: ooo.end,
        reason: { id: ooo.reason.id, name: ooo.reason.name ?? '' },
        notes: ooo.notes ?? ''
      }))
    }),
    ...(formData.outOfOfficesToUpdate?.length && {
      outOfOfficesToUpdate: formData.outOfOfficesToUpdate.map((ooo) => ({
        id: ooo.id!,
        employee: { id: ooo.employee!.id },
        startDate: ooo.start,
        endDate: ooo.end,
        reason: { id: ooo.reason.id, name: ooo.reason.name ?? '' },
        notes: ooo.notes ?? ''
      }))
    }),
    ...(formData.outOfOfficesToDelete?.length && {
      outOfOfficesToDelete: formData.outOfOfficesToDelete
    })
  } as Input_V1_Mutations_Employee_UpdateEmployeeMutationInput;
};

const mapJobRoles = (roles?: Maybe<Dtos_V1_CrewRoleDto[]>): JobRoleData[] => {
  return (
    roles?.map((role) => ({
      id: role.id,
      name: role.name ?? 'Unknown Role'
    })) ?? []
  );
};

// Map API employee to Employee type for EmployeeList
export function mapEmployeeFromApi(apiEmployee: Dtos_V1_EmployeeDto) {
  return {
    id: String(apiEmployee.id),
    name: apiEmployee.name ?? '',
    jobTitle:
      apiEmployee.jobRoles
        ?.map((r: Dtos_V1_CrewRoleDto) => r.name)
        .filter(Boolean)
        .join(', ') ?? '',
    status: apiEmployee.employmentStatus?.name ?? 'N/A',
    avatarUrl: '' // TODO: Add avatar URL mapping when field is available
  };
}
